import React, { useState, Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectCurrentlyEditingPointType,
    selectPlaceRoutePointOnClick,
    selectWorkingAddress,
    selectWorkingLatLng,
    selectWorkingLocationName,
    setPlaceRoutePointOnClick,
    setWorkingLatLng,
    setWorkingAddress,
    setWorkingZoneId,
    setWorkingMarkerId,
    setWorkingLocationName,
    setWorkingLocationSelectionType,
    selectGetVehicleLocationLoading,
    selectGetVehicleLocationError,
    getVehicleLocation,
    setIsMinimized,
    setPointSelectionMethod
} from '../../../features/tripcenter/googleRouteMakerSlice';
import { zonesSelector, zonesListSelector } from '../../../features/places/placesSlice';
import { markersSelector, markersListSelector } from '../../../features/places/placesSlice';
import { assetNamesSelector } from '../../../features/assets/assetsSlice';

import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const AddLocation = ({ onLocationComplete, onCancel, showParametersForm = false }) => {
    const dispatch = useDispatch();
    const currentlyEditingPointType = useSelector(selectCurrentlyEditingPointType);
    const placeRoutePointOnClick = useSelector(selectPlaceRoutePointOnClick);
    const workingAddress = useSelector(selectWorkingAddress);
    const workingLatLng = useSelector(selectWorkingLatLng);
    const workingLocationName = useSelector(selectWorkingLocationName);
    const zones = useSelector(zonesSelector);
    const markers = useSelector(markersSelector);
    const zoneList = useSelector(zonesListSelector);
    const markerList = useSelector(markersListSelector);
    const assetNames = useSelector(assetNamesSelector);
    const isVehicleLocationLoading = useSelector(selectGetVehicleLocationLoading);
    const vehicleLocationError = useSelector(selectGetVehicleLocationError);

    const [locationType, setLocationType] = useState('zone');
    const [selectedZone, setSelectedZone] = useState(null);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [locationName, setLocationName] = useState('');
    const [triggerRadius, setTriggerRadius] = useState(200);
    const [maxDetentionTime, setMaxDetentionTime] = useState(0);
    const [showFinalForm, setShowFinalForm] = useState(showParametersForm);
    const [mapSelectionActive, setMapSelectionActive] = useState(false);

    // New state for vehicle location
    const [vehicleTimeSelection, setVehicleTimeSelection] = useState('now');
    const [selectedVehicle, setSelectedVehicle] = useState('');
    const [selectedTimestamp, setSelectedTimestamp] = useState(dayjs());

    const [errors, setErrors] = useState({
        locationName: false,
        triggerRadius: false,
        maxDetentionTime: false,
        vehicle: false,
    });

    // Effect to track map selection state
    useEffect(() => {
        if (locationType === 'map' && placeRoutePointOnClick) {
            setMapSelectionActive(true);
        } else {
            setMapSelectionActive(false);
        }
    }, [locationType, placeRoutePointOnClick]);

    // Also add an effect to respond to prop changes
    useEffect(() => {
        if (showParametersForm && workingLatLng) {
            setShowFinalForm(true);
        }
    }, [showParametersForm, workingLatLng]);

    const handleLocationTypeChange = (event) => {
        const selectedType = event.target.value;
        setLocationType(selectedType);
        setShowFinalForm(false);
        setMapSelectionActive(false);

        // Clear all working states
        dispatch(setWorkingLatLng(null));
        dispatch(setWorkingAddress(null));
        dispatch(setWorkingZoneId(null));
        dispatch(setWorkingMarkerId(null));
        dispatch(setWorkingLocationName(null));

        // Set the point selection method and location type
        dispatch(setPointSelectionMethod(selectedType));

        if (selectedType === 'map') {
            // For map selection, do these in order:
            dispatch(setWorkingLocationSelectionType('point'));
            dispatch(setIsMinimized(true));

            // Force placeRoutePointOnClick to true and prevent immediate state changes
            setTimeout(() => {
                dispatch(setPlaceRoutePointOnClick(true));
            }, 0);
        } else {
            dispatch(setWorkingLocationSelectionType(selectedType));
            dispatch(setPlaceRoutePointOnClick(false));
            dispatch(setIsMinimized(false));
        }
    };

    const handleVehicleTimeSelectionChange = (event) => {
        setVehicleTimeSelection(event.target.value);
    };

    const handleVehicleSelect = (event) => {
        setSelectedVehicle(event.target.value);
        setErrors(prev => ({ ...prev, vehicle: false }));
    };

    const handleTimestampChange = (newValue) => {
        setSelectedTimestamp(newValue);
    };

    const handleGetVehicleLocation = () => {
        if (!selectedVehicle) {
            setErrors(prev => ({ ...prev, vehicle: true }));
            return;
        }

        // Calculate timestamp based on selection
        const timestamp = vehicleTimeSelection === 'now'
            ? Math.floor(Date.now() / 1000)
            : Math.floor(selectedTimestamp.valueOf() / 1000);

        // Dispatch action to get vehicle location
        dispatch(getVehicleLocation({
            assetId: selectedVehicle,
            timestamp: timestamp,
            type: vehicleTimeSelection
        })).then((result) => {
            // Check if the action was successful
            if (result.meta.requestStatus === 'fulfilled') {
                // Get the vehicle name for the location name field
                if (assetNames && assetNames[selectedVehicle]) {
                    const vehicleName = assetNames[selectedVehicle].regn;
                    setLocationName(`${vehicleName} Location`);
                }

                // Set placeRoutePointOnClick to true to compact the modal and allow marker dragging
                dispatch(setPlaceRoutePointOnClick(true));

                // Set map selection active to properly handle UI states
                setMapSelectionActive(true);
            }
        });
        handleVehicleLocationFetch();
    };

    const handleConfirmVehicleLocation = () => {
        if (workingLatLng) {
            let locationLabel = 'Vehicle Location';
            if (assetNames && assetNames[selectedVehicle]) {
                locationLabel = `${assetNames[selectedVehicle].regn} Location`;
            }
            setLocationName(locationLabel);
            dispatch(setWorkingLocationName(locationLabel));

            // Instead of setting showFinalForm, directly submit the location
            const locationData = {
                lat: workingLatLng.lat,
                lng: workingLatLng.lng,
                location: workingLatLng,
                address: workingAddress,
                locationName: locationLabel,
                locationType: 'point',
                triggerRadius: triggerRadius || 200,
                maxDetentionTime: maxDetentionTime || 0
            };

            // Turn off map selection mode
            dispatch(setPlaceRoutePointOnClick(false));

            // Call the completion handler directly
            onLocationComplete(locationData);
        }
    };

    const handleZoneSelect = (event, newValue) => {
        if (newValue) {
            const selectedZoneId = zoneList.find(zoneId => zones[zoneId].zone_name === newValue);

            if (selectedZoneId) {
                const selectedZone = zones[selectedZoneId];
                setSelectedZone(selectedZone);

                const zoneName = selectedZone.zone_name;
                setLocationName(zoneName);

                const coords = selectedZone.zone_coords;
                const centerLat = coords.reduce((sum, coord) => sum + coord.lat, 0) / coords.length;
                const centerLng = coords.reduce((sum, coord) => sum + coord.lng, 0) / coords.length;

                dispatch(setWorkingLatLng({ lat: centerLat, lng: centerLng }));
                dispatch(setWorkingAddress({ name: zoneName, isCustomName: true }));
                dispatch(setWorkingZoneId(selectedZoneId));
                dispatch(setWorkingLocationName(zoneName));
                dispatch(setWorkingLocationSelectionType('zone'));

                setShowFinalForm(true);
            }
        } else {
            setSelectedZone(null);
            setLocationName('');
            setShowFinalForm(false);
            // Clear working states
            dispatch(setWorkingLatLng(null));
            dispatch(setWorkingAddress(null));
            dispatch(setWorkingZoneId(null));
            dispatch(setWorkingLocationName(null));
        }
    };

    const handleMarkerSelect = (event, newValue) => {
        if (newValue) {
            const selectedMarkerId = markerList.find(markerId => markers[markerId].marker_name === newValue);

            if (selectedMarkerId) {
                const selectedMarker = markers[selectedMarkerId];
                setSelectedMarker(selectedMarker);

                const markerName = selectedMarker.marker_name;
                setLocationName(markerName);

                dispatch(setWorkingLatLng(selectedMarker.marker_coords));
                dispatch(setWorkingAddress({ name: markerName, isCustomName: true }));
                dispatch(setWorkingMarkerId(selectedMarkerId));
                dispatch(setWorkingLocationName(markerName));
                dispatch(setWorkingLocationSelectionType('point'));

                setShowFinalForm(true);
            }
        } else {
            setSelectedMarker(null);
            setLocationName('');
            setShowFinalForm(false);
            // Clear working states
            dispatch(setWorkingLatLng(null));
            dispatch(setWorkingAddress(null));
            dispatch(setWorkingMarkerId(null));
            dispatch(setWorkingLocationName(null));
        }
    };

    const handleDoneSelection = () => {
        if (workingLatLng && workingAddress) {
            const formattedName = workingAddress.name
                ? workingAddress.name.replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 20) + '...'
                : 'Selected Location';

            setLocationName(formattedName);
            dispatch(setWorkingLocationName(formattedName));
            dispatch(setWorkingLocationSelectionType('point'));

            // CRITICAL: First set showFinalForm to true
            setShowFinalForm(true);

            // THEN turn off map selection mode
            dispatch(setPlaceRoutePointOnClick(false));

            // Clear map selection active state
            setMapSelectionActive(false);
        }
    };

    const handleConfirmLocationOnMap = () => {
        if (workingLatLng) {
            const defaultName = workingAddress
                ? workingAddress.replace(/[^a-zA-Z0-9\s]/g, '').substring(0, 20) + '...'
                : 'Map Location';

            setLocationName(defaultName);
            dispatch(setWorkingLocationName(defaultName));

            // CRITICAL: First set showFinalForm to true
            setShowFinalForm(true);

            // THEN turn off map selection mode
            dispatch(setPlaceRoutePointOnClick(false));

            // Clear map selection active state
            setMapSelectionActive(false);
        }
    };

    const handleCancel = () => {
        // Reset map selection if active
        if (locationType === 'map') {
            dispatch(setPlaceRoutePointOnClick(false));
        }
        if (onCancel) {
            onCancel();
        }
    };

    const validateForm = () => {
        const newErrors = {
            locationName: !locationName && !workingLocationName,
            triggerRadius: locationType !== 'zone' && (!triggerRadius || triggerRadius < 200),
        };

        setErrors(newErrors);
        return !Object.values(newErrors).some(error => error);
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            return;
        }

        // Prepare location data with all necessary fields
        const locationData = {
            lat: workingLatLng.lat,
            lng: workingLatLng.lng,
            location: workingLatLng,
            address: workingAddress,
            locationName: locationName || workingLocationName,
            // Set correct locationType based on selection method
            locationType: locationType === 'zone' ? 'zone' : 'point',
            zoneCoordinates: locationType === 'zone' ? selectedZone?.zone_coords : null,
            triggerRadius: locationType !== 'zone' ? triggerRadius : null,
            maxDetentionTime
        };

        // Make sure to turn off map selection mode if it's active
        if (placeRoutePointOnClick) {
            dispatch(setPlaceRoutePointOnClick(false));
        }

        // Call the parent handler with the location data
        onLocationComplete(locationData);
    };

    // Update handleVehicleLocationFetch to include proper state management
    const handleVehicleLocationFetch = () => {
        dispatch(setIsMinimized(true));
        // Set the working location selection type to point
        dispatch(setWorkingLocationSelectionType('point'));
    };

    // Add useEffect to handle state cleanup
    useEffect(() => {
        return () => {
            // Cleanup when component unmounts
            dispatch(setPlaceRoutePointOnClick(false));
            dispatch(setWorkingLatLng(null));
            dispatch(setWorkingAddress(null));
        };
    }, [dispatch]);


    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h6" color='textPrimary'>Add {currentlyEditingPointType} Location</Typography>
            <br />
            <FormControl component="fieldset">
                <FormLabel>How would you like to select the {currentlyEditingPointType} location?</FormLabel>
                <RadioGroup row value={locationType} onChange={handleLocationTypeChange}>
                    <FormControlLabel value="zone" control={<Radio />} label="Select a Zone" />
                    <FormControlLabel value="marker" control={<Radio />} label="Select a Marker" />
                    <FormControlLabel value="map" control={<Radio />} label="Locate on Map" />
                    <FormControlLabel value="vehicle" control={<Radio />} label="Vehicle Location" />
                </RadioGroup>
            </FormControl>

            {locationType === 'zone' && (
                <Autocomplete
                    //TODO: filter for duplicate then show options, do for markers also
                    options={zoneList.map(zoneId => zones[zoneId].zone_name)}
                    renderInput={(params) => <TextField {...params} label="Select Zone" />}
                    onChange={handleZoneSelect}
                    sx={{ mt: 2 }}
                />
            )}

            {locationType === 'marker' && (
                <Autocomplete
                    options={markerList.map(markerId => markers[markerId].marker_name)}
                    renderInput={(params) => <TextField {...params} label="Select Marker" />}
                    onChange={handleMarkerSelect}
                    sx={{ mt: 2 }}
                />
            )}

            {locationType === 'vehicle' && (
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }} error={errors.vehicle}>
                        <InputLabel>Select Vehicle</InputLabel>
                        <Select
                            value={selectedVehicle}
                            onChange={handleVehicleSelect}
                            label="Select Vehicle"
                        >
                            {assetNames && Object.keys(assetNames).map((assetId) => (
                                <MenuItem key={assetId} value={assetId}>
                                    {assetNames[assetId].regn || assetNames[assetId].imei}
                                </MenuItem>
                            ))}
                        </Select>
                        {errors.vehicle && <Typography color="error" variant="caption">Please select a vehicle</Typography>}
                    </FormControl>

                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                        <FormLabel>Time Reference</FormLabel>
                        <RadioGroup row value={vehicleTimeSelection} onChange={handleVehicleTimeSelectionChange}>
                            <FormControlLabel value="now" control={<Radio />} label="Current Location" />
                            <FormControlLabel value="past" control={<Radio />} label="Past Location" />
                        </RadioGroup>
                    </FormControl>

                    {vehicleTimeSelection === 'past' && (
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DateTimePicker
                                label="Select Date & Time"
                                value={selectedTimestamp}
                                onChange={handleTimestampChange}
                                textField={(params) => <TextField {...params} fullWidth sx={{ mb: 2 }} />}
                                sx={{ mb: 2, width: '100%' }}
                            />
                        </LocalizationProvider>
                    )}

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGetVehicleLocation}
                        disabled={isVehicleLocationLoading || !selectedVehicle}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        {isVehicleLocationLoading ? (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                                Loading location...
                            </Box>
                        ) : (
                            'Get Vehicle Location'
                        )}
                    </Button>

                    {vehicleLocationError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {vehicleLocationError.message || 'Failed to get vehicle location. Please try again.'}
                        </Alert>
                    )}

                    {workingLatLng && locationType === 'vehicle' && !showFinalForm && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                Vehicle location found. You can drag the marker to adjust the position if needed.
                            </Alert>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleConfirmVehicleLocation}
                                fullWidth
                            >
                                Confirm Vehicle Location
                            </Button>
                        </Box>
                    )}
                </Box>
            )}

            {locationType === 'map' && !showFinalForm && (
                <Fragment>
                    <Typography variant="body1" color="primary" sx={{ mt: 2, animation: 'pulse 1.5s infinite' }}>
                        {workingLatLng ?
                            `Drag the red marker to adjust your ${currentlyEditingPointType} location` :
                            `Click on the map to set your ${currentlyEditingPointType} location`}
                    </Typography>

                    {mapSelectionActive && (
                        <Alert severity="info" sx={{ mt: 2 }}>
                            The location selection panel is minimized while you interact with the map.
                            Click the "Expand" button if you need to return to this view.
                        </Alert>
                    )}

                    {workingLatLng && !mapSelectionActive && !showFinalForm && (
                        <Button
                            variant="contained"
                            onClick={handleConfirmLocationOnMap}
                            sx={{ mt: 2 }}
                            fullWidth
                        >
                            Confirm Location Point
                        </Button>
                    )}
                </Fragment>
            )}

            {(locationType === 'map' || locationType === 'vehicle') && placeRoutePointOnClick && workingLatLng && !showFinalForm && !mapSelectionActive && (
                <Button
                    variant="contained"
                    onClick={locationType === 'vehicle' ? handleConfirmVehicleLocation : handleDoneSelection}
                    sx={{ mt: 2 }}
                    fullWidth
                >
                    Confirm Location Point
                </Button>
            )}

            {showFinalForm && (
                <Box sx={{ mt: 2 }}>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel error={errors.locationName}>Location Name</FormLabel>
                        <TextField
                            fullWidth
                            value={locationName ?? ''}
                            onChange={(e) => {
                                setLocationName(e.target.value);
                                setErrors(prev => ({ ...prev, locationName: false }));
                            }}
                            error={errors.locationName}
                            helperText={errors.locationName ? "Location name is required" : ""}
                        />
                    </FormControl>

                    {locationType !== 'zone' && (
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <FormLabel error={errors.triggerRadius}>Trigger Radius (meters)</FormLabel>
                            <TextField
                                fullWidth
                                type="number"
                                value={triggerRadius ?? 200}
                                onChange={(e) => {
                                    // Parse the input value as a number
                                    const inputValue = e.target.value === '' ? '' : Number(e.target.value);

                                    // If it's an empty string, set to empty temporarily to allow user typing
                                    if (inputValue === '') {
                                        setTriggerRadius('');
                                    } else {
                                        // Only apply the minimum constraint when user has finished typing
                                        setTriggerRadius(inputValue);
                                    }

                                    setErrors(prev => ({ ...prev, triggerRadius: false }));
                                }}
                                // Apply validation on blur rather than during typing
                                onBlur={() => {
                                    // Enforce minimum value when field loses focus
                                    if (triggerRadius === '' || triggerRadius < 200) {
                                        setTriggerRadius(200);
                                    }
                                }}
                                inputProps={{
                                    min: 200,
                                    // This prevents default browser arrows from enforcing min during typing
                                    step: 'any'
                                }}
                                error={errors.triggerRadius}
                                helperText={errors.triggerRadius ? "Trigger radius must be at least 200 meters" : "Minimum 200 meters"}
                                InputProps={{
                                    endAdornment: <InputAdornment position="end">Meters</InputAdornment>,
                                }}
                            />
                        </FormControl>
                    )}

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <FormLabel error={errors.maxDetentionTime}>Max Detention Time (hours)</FormLabel>
                        <TextField
                            fullWidth
                            type="number"
                            value={maxDetentionTime ?? 1}
                            onChange={(e) => {
                                setMaxDetentionTime(Math.max(0, Number(e.target.value)));
                                setErrors(prev => ({ ...prev, maxDetentionTime: false }));
                            }}
                            inputProps={{ min: 0 }}
                            error={errors.maxDetentionTime}
                            helperText={errors.maxDetentionTime ? "Max stoppage time must be at least 1 hour" : "Minimum 1 hour"}
                            InputProps={{
                                endAdornment: <InputAdornment position="end">Hours</InputAdornment>,
                            }}
                        />
                    </FormControl>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Button variant="outlined" onClick={handleCancel}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSubmit}>
                            Add Location
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default AddLocation; 